import { useRef, useEffect } from 'react';
import { cn } from '@/utils/cn';
import type { LeagueParticipant } from '../services/leagueService';

interface LeaderboardListProps {
    participants: LeagueParticipant[];
    currentUserId?: number;
    promotionRank: number;
    demotionRank: number;
}

export const LeaderboardList = ({
    participants,
    currentUserId,
    promotionRank,
    demotionRank
}: LeaderboardListProps) => {

    const toPersianDigits = (v: number | string) => String(v).replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);

    // Helper for trend icon (randomized for demo if not in data, but let's stick to static for now or logic)
    // The reference has green up arrows and red down arrows. 
    // Since we don't have "previous rank" in the API yet, we can just show a neutral dash or infer from points?
    // Actually, let's just use a placeholder logic or hide it if we don't have data.
    // The original code didn't have trends. I'll add a visual placeholder that looks nice but maybe neutral for now,
    // or just omit it to avoid fake data. 
    // Auto-scroll to current user
    const currentUserRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (currentUserRef.current) {
            currentUserRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [participants]);

    return (
        <div className="bg-white dark:bg-dark-bg-secondary rounded-t-[32px] min-h-[500px] pb-20 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] relative z-20">
            <div className="w-12 h-1.5 bg-gray-200 dark:bg-dark-border rounded-full mx-auto mt-3 mb-6" /> {/* Handle bar */}

            <div className="px-4 flex flex-col gap-2">
                {participants.map((participant) => (
                    <div key={participant.participant_id}>
                        {/* Promotion Zone Separator - Duolingo Style */}
                        {participant.rank === promotionRank + 1 && (
                            <div className="flex items-center justify-center gap-1.5 py-4 mb-2">
                                <span className="text-green-600 text-lg">▲</span>
                                <span className="text-xs font-bold text-green-600 uppercase tracking-wide font-persian">منطقه صعود</span>
                                <span className="text-green-600 text-lg">▲</span>
                            </div>
                        )}

                        {/* Demotion Zone Separator - Duolingo Style */}
                        {participant.rank === demotionRank + 1 && (
                            <div className="flex items-center justify-center gap-1.5 py-4 mb-2">
                                <span className="text-red-500 text-lg">▼</span>
                                <span className="text-xs font-bold text-red-500 uppercase tracking-wide font-persian">منطقه سقوط</span>
                                <span className="text-red-500 text-lg">▼</span>
                            </div>
                        )}

                        <div
                            ref={participant.is_current_user ? currentUserRef : null}
                            className={cn(
                                "flex items-center p-3 rounded-2xl transition-colors",
                                participant.is_current_user ? "bg-primary-500/20 dark:bg-primary-500/20 border-2 border-primary-500/40 dark:border-primary-500/40" : "hover:bg-gray-50 dark:hover:bg-dark-bg-tertiary"
                            )}
                        >
                            {/* Rank */}
                            <div className="w-8 text-center font-bold text-gray-400 dark:text-dark-muted font-persian text-sm">
                                {toPersianDigits(participant.rank)}
                            </div>

                            {/* Avatar */}
                            <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-dark-bg-tertiary mx-3 overflow-hidden border border-gray-100 dark:border-dark-border">
                                {participant.avatar_url ? (
                                    <img src={participant.avatar_url} alt={participant.name} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-lg">
                                        👤
                                    </div>
                                )}
                            </div>

                            {/* Name */}
                            <div className="flex-1">
                                <div className={cn(
                                    "font-bold text-sm font-persian",
                                    participant.is_current_user ? "text-primary-600 dark:text-primary-500" : "text-gray-700 dark:text-dark-foreground"
                                )}>
                                    {participant.name}
                                </div>
                            </div>

                            {/* XP Score */}
                            <div className="text-xs text-gray-500 dark:text-dark-muted font-persian">
                                {toPersianDigits(participant.points_in_cycle)} امتیاز
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
